import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import StripeService from '../services/StripeService';

const useSubscription = (user) => {
  const [subscriptionStatus, setSubscriptionStatus] = useState({
    isValid: false,
    isLoading: true,
    status: 'unknown',
    maxAccounts: 1,
    maxVideosPerMonth: 10,
    videosUsedThisMonth: 0,
    error: null,
  });

  const verifySubscription = async () => {
    if (!user) {
      setSubscriptionStatus(prev => ({
        ...prev,
        isLoading: false,
        isValid: false,
        status: 'no_user',
      }));
      return;
    }

    try {
      setSubscriptionStatus(prev => ({ ...prev, isLoading: true, error: null }));
      
      // PRIORITY 1: Check Firebase first - this is the source of truth
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        // New user - allow them to select free plan
        setSubscriptionStatus({
          isValid: false,
          isLoading: false,
          status: 'new_user',
          maxAccounts: 1,
          maxVideosPerMonth: 10,
          videosUsedThisMonth: 0,
          error: null,
        });
        return;
      }

      const userData = userDoc.data();
      
      // PRIORITY 2: Check for unlimited status FIRST
      if (userData.isUnlimited) {
        setSubscriptionStatus({
          isValid: true,
          isLoading: false,
          status: 'unlimited',
          isUnlimited: true,
          maxAccounts: 999,
          maxVideosPerMonth: -1, // Unlimited
          videosUsedThisMonth: userData.videosUsedThisMonth || 0,
          subscriptionType: 'unlimited',
          isSubscribed: true,
          error: null,
        });
        return;
      }

      // PRIORITY 3: Check for special permissions (dev, etc.)
      if (userData.isDev) {
        setSubscriptionStatus({
          isValid: true,
          isLoading: false,
          status: 'developer',
          isDev: true,
          maxAccounts: 999,
          maxVideosPerMonth: -1, // Unlimited
          videosUsedThisMonth: userData.videosUsedThisMonth || 0,
          error: null,
        });
        return;
      }

      // PRIORITY 4: Check Firebase subscription status
      if (userData.isSubscribed && userData.subscriptionType) {
        setSubscriptionStatus({
          isValid: true,
          isLoading: false,
          status: userData.subscriptionType,
          subscriptionType: userData.subscriptionType,
          isSubscribed: userData.isSubscribed,
          maxAccounts: userData.maxAccounts || 1,
          maxVideosPerMonth: userData.maxVideosPerMonth || 10,
          videosUsedThisMonth: userData.videosUsedThisMonth || 0,
          error: null,
        });
        return;
      }

      // PRIORITY 5: Only verify with Stripe if they have a Stripe subscription ID
      if (userData.stripeSubscriptionId) {
        try {
          const stripeResult = await StripeService.verifySubscription(user.uid);
          setSubscriptionStatus({
            isValid: stripeResult.isValid,
            isLoading: false,
            status: stripeResult.status,
            maxAccounts: stripeResult.maxAccounts || 1,
            maxVideosPerMonth: stripeResult.maxVideosPerMonth || 10,
            videosUsedThisMonth: stripeResult.videosUsedThisMonth || 0,
            currentPeriodEnd: stripeResult.currentPeriodEnd,
            error: null,
          });
        } catch (stripeError) {
          console.warn('Stripe verification failed, falling back to Firebase data:', stripeError);
          // Fall back to Firebase data if Stripe fails
          setSubscriptionStatus({
            isValid: false,
            isLoading: false,
            status: 'needs_subscription',
            maxAccounts: 1,
            maxVideosPerMonth: 10,
            videosUsedThisMonth: userData.videosUsedThisMonth || 0,
            error: null,
          });
        }
        return;
      }

      // PRIORITY 6: Default - no subscription, allow free plan selection
      setSubscriptionStatus({
        isValid: false,
        isLoading: false,
        status: 'needs_subscription',
        maxAccounts: 1,
        maxVideosPerMonth: 10,
        videosUsedThisMonth: userData.videosUsedThisMonth || 0,
        error: null,
      });

    } catch (error) {
      console.error('Error checking subscription:', error);
      // On error, allow access with basic limits rather than blocking
      setSubscriptionStatus({
        isValid: false,
        isLoading: false,
        status: 'error',
        maxAccounts: 1,
        maxVideosPerMonth: 10,
        videosUsedThisMonth: 0,
        error: error.message,
      });
    }
  };

  useEffect(() => {
    verifySubscription();
  }, [user]);

  const refreshSubscription = () => {
    verifySubscription();
  };

  return { subscriptionStatus, refreshSubscription };
};

export default useSubscription; 